/** @format */
import { ChangeDetectionStrategy, Component, inject, Inject, OnInit, ViewEncapsulation } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { FudisDialogService } from '@funidata/ngx-fudis';
import { StateService } from '@uirouter/angular';
import { ValidatablePlan } from 'common-typescript';
import { CurriculumPeriod, Module, StudyRight } from 'common-typescript/types';
import { combineLatest, map, Observable, OperatorFunction } from 'rxjs';
import { finalize, take } from 'rxjs/operators';
import { LocaleService } from 'sis-common';
import { AppErrorHandler, CurriculumPeriodEntityService } from 'sis-components';
import { ModuleInfoService } from '../../module-info.service';

export interface StudentModuleInfoVersionChangeWarningValues {
    fromModule: Module;
    toModule: Module;
    validatablePlan: ValidatablePlan;
    studyRight: StudyRight;
}

export type StudentModuleInfoVersionChangeResult = { action: 'copied' | 'replaced' };

interface StudentModuleInfoVersionWarningData {
    fromCurriculumPeriods: string;
    toCurriculumPeriods: string;
}

/**
 * Opener for the decoupled "copy plan and switch version, or just switch version" dialog used by the new
 * StudentModuleInfoModalComponent. Unlike the legacy ModuleInfoVersionChangeWarningComponent (NgbModal reading the
 * AngularJS uiContext), this variant is a FudisDialog that receives the plan and study right explicitly.
 */
export function studentModuleInfoVersionChangeWarningOpener(): (
    values: StudentModuleInfoVersionChangeWarningValues,
) => MatDialogRef<StudentModuleInfoVersionChangeWarningComponent, StudentModuleInfoVersionChangeResult> {
    const fudisDialogService = inject(FudisDialogService);
    return values =>
        fudisDialogService.open(StudentModuleInfoVersionChangeWarningComponent, {
            data: values,
        }) as MatDialogRef<StudentModuleInfoVersionChangeWarningComponent, StudentModuleInfoVersionChangeResult>;
}

@Component({
    selector: 'app-student-module-info-version-change-warning',
    templateUrl: './student-module-info-version-change-warning.component.html',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class StudentModuleInfoVersionChangeWarningComponent implements OnInit {
    data$: Observable<StudentModuleInfoVersionWarningData>;
    pending = false;

    private readonly moduleInfoService = inject(ModuleInfoService);
    private readonly localeService = inject(LocaleService);
    private readonly stateService = inject(StateService);
    private readonly appErrorHandler = inject(AppErrorHandler);
    private readonly curriculumPeriodEntityService = inject(CurriculumPeriodEntityService);
    private readonly dialogRef =
        inject<MatDialogRef<StudentModuleInfoVersionChangeWarningComponent, StudentModuleInfoVersionChangeResult>>(MatDialogRef);

    constructor(@Inject(MAT_DIALOG_DATA) readonly values: StudentModuleInfoVersionChangeWarningValues) {}

    ngOnInit(): void {
        this.data$ = combineLatest({
            fromCurriculumPeriods: this.curriculumPeriodEntityService
                .getByIds(this.values.fromModule.curriculumPeriodIds)
                .pipe(this.getCurriculumPeriodsAsString()),
            toCurriculumPeriods: this.curriculumPeriodEntityService
                .getByIds(this.values.toModule.curriculumPeriodIds)
                .pipe(this.getCurriculumPeriodsAsString()),
        });
    }

    getCurriculumPeriodsAsString(): OperatorFunction<CurriculumPeriod[], string> {
        return map(curriculumPeriods =>
            curriculumPeriods.map(curriculumPeriod => this.localeService.localize(curriculumPeriod.abbreviation)).join(', '),
        );
    }

    copyAndReplace(): void {
        if (this.pending) {
            return;
        }
        this.pending = true;
        this.moduleInfoService
            .copyPlanAndReplaceModuleForPlan(
                this.values.fromModule,
                this.values.toModule,
                this.values.validatablePlan,
                this.values.studyRight,
            )
            // defaultErrorHandler swallows errors into EMPTY, so re-enable the actions on completion (incl. failure)
            // rather than in an error callback that would never fire; on success the dialog closes anyway.
            .pipe(
                take(1),
                this.appErrorHandler.defaultErrorHandler(),
                finalize(() => {
                    this.pending = false;
                }),
            )
            .subscribe(newPlan => {
                this.stateService.go('student.logged-in.structure', { planId: newPlan.id });
                this.dialogRef.close({ action: 'copied' });
            });
    }

    replace(): void {
        if (this.pending) {
            return;
        }
        this.pending = true;
        this.moduleInfoService
            .replaceModuleForPlan(this.values.fromModule, this.values.toModule, this.values.validatablePlan, this.values.studyRight)
            // See copyAndReplace: re-enable the actions on completion since defaultErrorHandler never emits an error.
            .pipe(
                take(1),
                this.appErrorHandler.defaultErrorHandler(),
                finalize(() => {
                    this.pending = false;
                }),
            )
            .subscribe(() => this.dialogRef.close({ action: 'replaced' }));
    }

    cancel(): void {
        this.dialogRef.close();
    }
}
